import { FormEvent, useState } from "react";

import type {
  MasterControlChromaDryRun,
  MasterControlDangerActions,
  MasterControlDryRun,
  MasterControlOrgDryRun,
  MasterControlRedisDryRun,
  MasterControlSyncDryRun,
  MasterControlUserDryRun,
  MasterControlWorkflowDryRun,
} from "@/hooks/useMasterControl";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import MCSecurityRiskBanner from "./MCSecurityRiskBanner";
import MCSecurityConfirmModal from "./MCSecurityConfirmModal";
import { DryRunDeleteUserModal } from "./DryRunDeleteUserModal";
import { DryRunDeleteOrgModal } from "./DryRunDeleteOrgModal";
import { DryRunRedisModal } from "./DryRunRedisModal";
import { DryRunChromaModal } from "./DryRunChromaModal";
import { DryRunWorkflowModal } from "./DryRunWorkflowModal";
import { DryRunSyncModal } from "./DryRunSyncModal";

type MCDangerZoneProps = {
  actions: MasterControlDangerActions;
  dryRun: MasterControlDryRun;
  onActionComplete?: () => void;
};

export function MCDangerZone({ actions, dryRun, onActionComplete }: MCDangerZoneProps) {
  const [userInput, setUserInput] = useState("");
  const [orgInput, setOrgInput] = useState("");
  const [agentInput, setAgentInput] = useState("");
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [userPreview, setUserPreview] = useState<MasterControlUserDryRun | null>(null);
  const [orgPreview, setOrgPreview] = useState<MasterControlOrgDryRun | null>(null);
  const [redisPreview, setRedisPreview] = useState<MasterControlRedisDryRun | null>(null);
  const [chromaPreview, setChromaPreview] = useState<MasterControlChromaDryRun | null>(null);
  const [workflowPreview, setWorkflowPreview] = useState<MasterControlWorkflowDryRun | null>(null);
  const [syncPreview, setSyncPreview] = useState<MasterControlSyncDryRun | null>(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [orgModalOpen, setOrgModalOpen] = useState(false);
  const [redisModalOpen, setRedisModalOpen] = useState(false);
  const [chromaModalOpen, setChromaModalOpen] = useState(false);
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [userTarget, setUserTarget] = useState("");
  const [orgTarget, setOrgTarget] = useState("");
  const [chromaTarget, setChromaTarget] = useState("");
  const [securityRequest, setSecurityRequest] = useState<{
    actionKey: string;
    phrase: string;
    label: string;
    run: () => Promise<void>;
  } | null>(null);
  const [securityModalOpen, setSecurityModalOpen] = useState(false);

  const runAction = async (actionKey: string, run: () => Promise<void>) => {
    setBusyAction(actionKey);
    setError(null);
    try {
      await run();
      onActionComplete?.();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
      return false;
    } finally {
      setBusyAction(null);
    }
  };

  const requestSecurityConfirmation = (
    actionKey: string,
    phrase: string,
    label: string,
    run: () => Promise<void>,
  ) => {
    setSecurityRequest({ actionKey, phrase, label, run });
    setSecurityModalOpen(true);
  };

  const handleSecurityConfirm = async () => {
    if (!securityRequest) return;
    const success = await runAction(securityRequest.actionKey, securityRequest.run);
    if (success) {
      setSecurityModalOpen(false);
      setSecurityRequest(null);
    }
  };

  const handleDeleteUser = async (event: FormEvent) => {
    event.preventDefault();
    if (!userInput.trim()) {
      setError("User ID or email required.");
      return;
    }
    setBusyAction("previewUser");
    setError(null);
    try {
      const preview = await dryRun.userDeletion(userInput.trim());
      setUserPreview(preview);
      setUserTarget(userInput.trim());
      setUserModalOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load preview.");
    } finally {
      setBusyAction(null);
    }
  };

  const handleDeleteOrg = async (event: FormEvent) => {
    event.preventDefault();
    if (!orgInput.trim()) {
      setError("Organization ID required.");
      return;
    }
    setBusyAction("previewOrg");
    setError(null);
    try {
      const preview = await dryRun.organizationDeletion(orgInput.trim());
      setOrgPreview(preview);
      setOrgTarget(orgInput.trim());
      setOrgModalOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load preview.");
    } finally {
      setBusyAction(null);
    }
  };

  const handlePurgeAgent = async (event: FormEvent) => {
    event.preventDefault();
    if (!agentInput.trim()) {
      setError("Agent ID required.");
      return;
    }
    setBusyAction("previewChroma");
    setError(null);
    try {
      const preview = await dryRun.chroma(agentInput.trim());
      setChromaPreview(preview);
      setChromaTarget(agentInput.trim());
      setChromaModalOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load preview.");
    } finally {
      setBusyAction(null);
    }
  };

  const handleRedisPreview = async () => {
    setBusyAction("previewRedis");
    setError(null);
    try {
      const preview = await dryRun.redis();
      setRedisPreview(preview);
      setRedisModalOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load preview.");
    } finally {
      setBusyAction(null);
    }
  };

  const handleWorkflowPreview = async () => {
    setBusyAction("previewWorkflow");
    setError(null);
    try {
      const preview = await dryRun.workflows();
      setWorkflowPreview(preview);
      setWorkflowModalOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load preview.");
    } finally {
      setBusyAction(null);
    }
  };

  const handleSyncPreview = async () => {
    setBusyAction("previewSync");
    setError(null);
    try {
      const preview = await dryRun.sync();
      setSyncPreview(preview);
      setSyncModalOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load preview.");
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <Card className="rounded-2xl border border-rose-900 bg-rose-950/10 shadow-none">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-rose-200">Danger zone</CardTitle>
        <p className="text-sm text-rose-200/70">Owner-only destructive actions. Double-check tenant before firing.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <MCSecurityRiskBanner />
        <div className="grid gap-4 md:grid-cols-3">
          <form className="space-y-2 rounded-2xl border border-rose-900/60 bg-rose-950/20 p-4" onSubmit={handleDeleteUser}>
            <p className="text-xs uppercase tracking-wide text-rose-200/80">Delete user</p>
            <Input
              value={userInput}
              onChange={(event) => setUserInput(event.target.value)}
              placeholder="user@example.com or user-id"
              className="bg-background text-foreground"
            />
            <Button type="submit" variant="destructive" size="sm" disabled={busyAction === "deleteUser"}>
              {busyAction === "deleteUser" ? "Deleting…" : "Delete user"}
            </Button>
          </form>
          <form className="space-y-2 rounded-2xl border border-rose-900/60 bg-rose-950/20 p-4" onSubmit={handleDeleteOrg}>
            <p className="text-xs uppercase tracking-wide text-rose-200/80">Delete organization</p>
            <Input
              value={orgInput}
              onChange={(event) => setOrgInput(event.target.value)}
              placeholder="org_123"
              className="bg-background text-foreground"
            />
            <Button type="submit" variant="destructive" size="sm" disabled={busyAction === "deleteOrg"}>
              {busyAction === "deleteOrg" ? "Deleting…" : "Delete org"}
            </Button>
          </form>
          <form className="space-y-2 rounded-2xl border border-rose-900/60 bg-rose-950/20 p-4" onSubmit={handlePurgeAgent}>
            <p className="text-xs uppercase tracking-wide text-rose-200/80">Purge Chroma</p>
            <Input
              value={agentInput}
              onChange={(event) => setAgentInput(event.target.value)}
              placeholder="manager"
              className="bg-background text-foreground"
            />
            <Button type="submit" variant="destructive" size="sm" disabled={busyAction === "purgeAgent"}>
              {busyAction === "purgeAgent" ? "Dispatching…" : "Purge memory"}
            </Button>
          </form>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <Button
            type="button"
            variant="destructive"
            className="rounded-2xl border border-rose-900/60 bg-rose-900/30 py-6 text-base font-semibold text-rose-100"
            disabled={busyAction === "previewRedis"}
            onClick={() => void handleRedisPreview()}
          >
            {busyAction === "previewRedis" ? "Loading…" : "Clear Redis queues"}
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="rounded-2xl border border-rose-900/60 bg-rose-900/30 py-6 text-base font-semibold text-rose-100"
            disabled={busyAction === "previewWorkflow"}
            onClick={() => void handleWorkflowPreview()}
          >
            {busyAction === "previewWorkflow" ? "Loading…" : "Reset workflow engine"}
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="rounded-2xl border border-rose-900/60 bg-rose-900/30 py-6 text-base font-semibold text-rose-100"
            disabled={busyAction === "previewSync"}
            onClick={() => void handleSyncPreview()}
          >
            {busyAction === "previewSync" ? "Loading…" : "Trigger global resync"}
          </Button>
        </div>
        {error && <p className="text-sm text-rose-200">{error}</p>}
      </CardContent>

      <DryRunDeleteUserModal
        open={userModalOpen}
        preview={userPreview}
        targetId={userTarget}
        onOpenChange={(open) => {
          if (!open) {
            setUserModalOpen(false);
            setUserPreview(null);
          } else {
            setUserModalOpen(true);
          }
        }}
        confirming={busyAction === "deleteUser"}
        onConfirm={() =>
          requestSecurityConfirmation("deleteUser", "DELETE USER", "Delete user", async () => {
            await actions.deleteUser(userTarget);
            setUserModalOpen(false);
            setUserPreview(null);
            setUserInput("");
          })
        }
      />

      <DryRunDeleteOrgModal
        open={orgModalOpen}
        preview={orgPreview}
        targetId={orgTarget}
        onOpenChange={(open) => {
          if (!open) {
            setOrgModalOpen(false);
            setOrgPreview(null);
          } else {
            setOrgModalOpen(true);
          }
        }}
        confirming={busyAction === "deleteOrg"}
        onConfirm={() =>
          requestSecurityConfirmation("deleteOrg", "DELETE ORGANIZATION", "Delete organization", async () => {
            await actions.deleteOrganization(orgTarget);
            setOrgModalOpen(false);
            setOrgPreview(null);
            setOrgInput("");
          })
        }
      />

      <DryRunRedisModal
        open={redisModalOpen}
        preview={redisPreview}
        onOpenChange={(open) => {
          if (!open) {
            setRedisModalOpen(false);
            setRedisPreview(null);
          } else {
            setRedisModalOpen(true);
          }
        }}
        confirming={busyAction === "redis"}
        onConfirm={() =>
          requestSecurityConfirmation("redis", "CLEAR REDIS", "Clear Redis queues", async () => {
            await actions.clearRedisQueues();
            setRedisModalOpen(false);
            setRedisPreview(null);
          })
        }
      />

      <DryRunChromaModal
        open={chromaModalOpen}
        preview={chromaPreview}
        agentId={chromaTarget}
        onOpenChange={(open) => {
          if (!open) {
            setChromaModalOpen(false);
            setChromaPreview(null);
          } else {
            setChromaModalOpen(true);
          }
        }}
        confirming={busyAction === "purgeAgent"}
        onConfirm={() =>
          requestSecurityConfirmation("purgeAgent", "PURGE MEMORY", "Purge agent memory", async () => {
            await actions.purgeAgentMemory(chromaTarget);
            setChromaModalOpen(false);
            setChromaPreview(null);
            setAgentInput("");
          })
        }
      />

      <DryRunWorkflowModal
        open={workflowModalOpen}
        preview={workflowPreview}
        onOpenChange={(open) => {
          if (!open) {
            setWorkflowModalOpen(false);
            setWorkflowPreview(null);
          } else {
            setWorkflowModalOpen(true);
          }
        }}
        confirming={busyAction === "workflow"}
        onConfirm={() =>
          requestSecurityConfirmation("workflow", "RESET WORKFLOWS", "Reset workflows", async () => {
            await actions.resetWorkflowEngine();
            setWorkflowModalOpen(false);
            setWorkflowPreview(null);
          })
        }
      />

      <DryRunSyncModal
        open={syncModalOpen}
        preview={syncPreview}
        onOpenChange={(open) => {
          if (!open) {
            setSyncModalOpen(false);
            setSyncPreview(null);
          } else {
            setSyncModalOpen(true);
          }
        }}
        confirming={busyAction === "resync"}
        onConfirm={() =>
          requestSecurityConfirmation("resync", "TRIGGER RESYNC", "Trigger global resync", async () => {
            await actions.triggerGlobalResync();
            setSyncModalOpen(false);
            setSyncPreview(null);
          })
        }
      />

      <MCSecurityConfirmModal
        open={securityModalOpen && Boolean(securityRequest)}
        onOpenChange={(open) => {
          if (!open) {
            setSecurityModalOpen(false);
            setSecurityRequest(null);
          } else {
            setSecurityModalOpen(true);
          }
        }}
        actionLabel={securityRequest?.label ?? "Dangerous action"}
        confirmationPhrase={securityRequest?.phrase ?? ""}
        onConfirm={handleSecurityConfirm}
        confirming={securityRequest ? busyAction === securityRequest.actionKey : false}
      />
    </Card>
  );
}

export default MCDangerZone;
